import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ApexOptions, ChartComponent } from 'ng-apexcharts';
import { BadgeModule } from '@primeng/badge';
import { BaseComponent } from '@common/classes/base-component/base.component';
import { UnitTypes } from '@modules/universal-billing/smart-meter/components/enums/unit-types.enum';
import { UnitTypeDirective } from '@common/directive/unit-type.directive';
import { UnitTypesInterface } from '@modules/settings/unit-types/unit-types.interface';

interface Meter {
  id: number;
  unitTypeId: number;
  installationReading: number;
  connectivityStatus: string;
  apartmentId?: number;
  installationLocation: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  installationDate: string;
  updatedAt: string;
  notes: string;
  unitType: UnitTypesInterface;
  last7DaysReadingValues: { readingDate: string; readingValue: number }[];
}

@Component({
  selector: 'app-counter-details',
  standalone: true,
  imports: [FormsModule, TranslatePipe, DatePipe, ChartComponent, BadgeModule, NgClass, UnitTypeDirective],
  providers: [DatePipe],
  templateUrl: './counter-details.html',
  styleUrl: './counter-details.scss'
})
export class CounterDetails extends BaseComponent implements OnInit {
  @Input() meter!: Meter;

  public areaChartOptionsByWeek: ApexOptions;

  constructor(private datePipe: DatePipe) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.initializeAreaChart(this.getReadingValuesByDate(this.meter.last7DaysReadingValues));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private initializeAreaChart(readings: { values: number[], dates: string[] }): void {
    this.areaChartOptionsByWeek = {
      series: [{
        name: "Counter",
        data: readings.values
      }],
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      stroke: {
        curve: 'smooth',
        width: 3.5
      },
      grid: {
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } }
      },
      xaxis: {
        categories: readings.dates
      },
      yaxis: {},
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        offsetX: 0,
        offsetY: 0,
        show: true
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: 0.4,
          opacityTo: 0.3,
          stops: [30, 100, 100, 100]
        }
      },
      dataLabels: { enabled: false },
      tooltip: {
        fixed: { enabled: false },
        x: {
          show: true,
          formatter: (val: any) => String(val)
        },
        y: {
          title: { formatter: () => '' }
        },
        marker: { show: false }
      },
      colors: ['#4f95e5']
    };
  }

  getReadingValuesByDate(data: any[]): { values: number[], dates: string[] } {
    const today = new Date();
    const dates = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return this.datePipe.transform(date, 'dd-MM') ?? date.toISOString().split('T')[0];
    }).reverse();
    const values = new Array(7).fill(0);

    if (data && Array.isArray(data)) {
      data.forEach(item => {
        if (item && item.readingDate && typeof item.readingValue === 'number') {
          const date = new Date(item.readingDate);
          const formattedDate = this.datePipe.transform(date, 'dd-MM') ?? date.toISOString().split('T')[0];
          const index = dates.indexOf(formattedDate);
          if (index !== -1) {
            values[index] = item.readingValue;
          }
        }
      });
    }

    return { values, dates };
  }

  getUnitTypeName(id: number): string {
    const unitTypeMap = {
      [UnitTypes.HEATING]: this.translateService.instant('UNITTYPES.HEATING'),
      [UnitTypes.COLD_WATER]: this.translateService.instant('UNITTYPES.COLD_WATER'),
      [UnitTypes.WARM_WATER]: this.translateService.instant('UNITTYPES.WARM_WATER')
    };
    return unitTypeMap[id] || '';
  }
}
